import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Lock, CheckCircle, Shield, Sparkles, AlertTriangle, RefreshCw, Loader2 } from 'lucide-react'
import PasswordStrengthIndicator from '../../components/ui/PasswordStrengthIndicator'
import { validatePassword } from '../../utils/passwordValidation'
import { Turnstile } from '@marsidev/react-turnstile'
import { supabase } from '../../config/supabase'
import { trackPasswordResetComplete } from '../../config/analyticsEvents'

const ResetPassword = () => {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [linkExpired, setLinkExpired] = useState(false)
  const [linkError, setLinkError] = useState('')
  const turnstileRef = useRef()
  
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { updatePassword } = useAuth()

  // Function to parse URL fragment errors
  const parseUrlErrors = () => {
    const fragment = window.location.hash.substring(1)
    if (!fragment) return null

    const params = new URLSearchParams(fragment)
    const error = params.get('error')
    const errorCode = params.get('error_code')
    const errorDescription = params.get('error_description')

    if (error) {
      return {
        error,
        errorCode,
        errorDescription: errorDescription ? decodeURIComponent(errorDescription.replace(/\+/g, ' ')) : ''
      }
    }
    return null
  }

  useEffect(() => {
    // Check if user is already authenticated (Supabase processed the token)
    const checkAuthState = async () => {
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (session && session.user) {
        // User is authenticated, which means the reset token was valid and processed
        return
      }

      // If no session, check for tokens in URL
      let token = searchParams.get('token')
      let type = searchParams.get('type')
      
      // If not found in query params, check hash fragment
      if (!token || !type) {
        const fragment = window.location.hash.substring(1)
        if (fragment) {
          const hashParams = new URLSearchParams(fragment)
          token = token || hashParams.get('access_token') || hashParams.get('token')
          type = type || hashParams.get('type')
        }
      }
      
      
      // If we have valid tokens, proceed with verification
      if (token && type === 'recovery') {
        try {
          const { data, error } = await supabase.auth.verifyOtp({
            token: token,
            type: 'recovery'
          })
          
          if (error) {
            console.error('Token verification error:', error)
            setLinkExpired(true)
            setLinkError('Your password reset link has expired or is invalid. Please request a new one.')
          }
        } catch (error) {
          console.error('Error exchanging token:', error)
          setLinkExpired(true)
          setLinkError('There was an error verifying your password reset link. Please try again.')
        }
        return
      }

      // Check for URL fragment errors
      const fragmentError = parseUrlErrors()
      if (fragmentError) {
        console.error('URL fragment error detected:', fragmentError)
        setLinkExpired(true)
        if (fragmentError.errorCode === 'otp_expired') {
          setLinkError('Your password reset link has expired. Please request a new one.')
        } else if (fragmentError.error === 'access_denied') {
          setLinkError('This password reset link is invalid or has already been used.')
        } else {
          setLinkError(fragmentError.errorDescription || 'There was an error with your password reset link.')
        }
        return
      }

      // If no valid tokens and no specific errors, show generic error
      console.error('No valid session or tokens found')
      setLinkExpired(true)
      setLinkError('Invalid or missing password reset link. Please request a new one.')
    }

    checkAuthState()
  }, [searchParams, navigate])

  const validateForm = () => {
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return false
    }
    
    const passwordValidation = validatePassword(password)
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors[0])
      return false
    }
    
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    if (!captchaToken) {
      setError('Please complete the CAPTCHA verification')
      return
    }

    setLoading(true)
    setError('')

    const { error } = await updatePassword(password, captchaToken)
    
    if (error) {
      setError(error.message)
      if (turnstileRef.current) {
        turnstileRef.current.reset()
        setCaptchaToken('')
      }
    } else {
      trackPasswordResetComplete()
      setSuccess(true)
      setTimeout(() => navigate('/'), 3000)
    }
    
    setLoading(false)
  }

  // Show error screen for expired or invalid links
  if (linkExpired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center p-4">
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 w-full max-w-md text-center animate-fade-in">
          <div className="relative mb-8 p-4">
            <div className="absolute inset-0 bg-red-500/20 rounded-full animate-pulse"></div>
            <AlertTriangle className="relative mx-auto h-10 w-10 text-red-500 mb-2" />
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent mb-4">
            Reset Link Issue
          </h2>
          <p className="text-secondary-600 mb-6">
            {linkError}
          </p>
          <div className="space-y-4">
            <button
              onClick={() => navigate('/auth')}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 px-4 rounded-xl font-semibold focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
            >
              Request New Reset Link
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full border-2 border-blue-200 text-blue-600 hover:bg-blue-50 py-3 px-4 rounded-xl font-semibold transition-all duration-200 hover:border-blue-300"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center p-4">
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 w-full max-w-md text-center animate-fade-in">
          <div className="relative mb-8">
            <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping"></div>
            <CheckCircle className="relative mx-auto h-16 w-16 text-green-500 mb-2" />
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-primary-600 bg-clip-text text-transparent mb-4">
            Password Updated!
          </h2>
          <p className="text-secondary-600 mb-6">
            Your password has been successfully updated. You will be redirected to the homepage in a moment.
          </p>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-primary-500 h-2 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center p-4">
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 w-full max-w-md animate-slide-up">
        <div className="text-center mb-8">
          <div className="relative mb-6">
            <div className="absolute inset-0 rounded-full blur-xl"></div>
            <div className="relative bg-gradient-to-br from-primary-500 to-accent-500 p-4 rounded-2xl w-16 h-16 mx-auto flex items-center justify-center">
              <Shield className="h-8 w-8 text-white" />
            </div>
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent mb-2">
            Create New Password
          </h2>
          <p className="text-secondary-600">Choose a strong password to secure your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-semibold text-secondary-700 mb-2">
              New Password
            </label>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-accent-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-secondary-400 h-5 w-5 transition-colors group-focus-within:text-primary-500" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 pr-12 w-full px-4 py-4 border-2 border-secondary-200 bg-white/80 rounded-xl focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-all duration-300 placeholder-secondary-400"
                  placeholder="Enter your new secure password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-primary-500 transition-colors duration-200 p-1"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <PasswordStrengthIndicator password={password} className="mt-4" />
          </div>

          <div className="space-y-2">
            <label htmlFor="confirmPassword" className="block text-sm font-semibold text-secondary-700 mb-2">
              Confirm Password
            </label>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-accent-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-secondary-400 h-5 w-5 transition-colors group-focus-within:text-primary-500" />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`pl-12 pr-12 w-full px-4 py-4 border-2 bg-white/80 rounded-xl focus:ring-2 focus:ring-primary-500/30 transition-all duration-300 placeholder-secondary-400 ${
                    confirmPassword && password !== confirmPassword 
                      ? 'border-red-300 focus:border-red-500' 
                      : confirmPassword && password === confirmPassword
                      ? 'border-green-300 focus:border-green-500'
                      : 'border-secondary-200 focus:border-primary-500'
                  }`}
                  placeholder="Confirm your new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-primary-500 transition-colors duration-200 p-1"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            {confirmPassword && password !== confirmPassword && (
              <p className="text-red-500 text-sm mt-2 animate-fade-in">Passwords do not match</p>
            )}
            {confirmPassword && password === confirmPassword && (
              <p className="text-green-600 text-sm mt-2 animate-fade-in flex items-center">
                <CheckCircle className="h-4 w-4 mr-1" />
                Passwords match
              </p>
            )}
          </div>

          {/* Cloudflare Turnstile CAPTCHA */}
          <div className="w-full overflow-hidden pr-[4px] py-1">
            <div className="flex justify-center">
              <Turnstile
                ref={turnstileRef}
                siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
                onSuccess={(token) => {
                  console.log('CAPTCHA verification successful')
                  setCaptchaToken(token)
                }}
                onError={() => {
                  console.error('CAPTCHA verification failed')
                  setCaptchaToken('')
                  setError('CAPTCHA verification failed. Please try again.')
                }}
                onExpire={() => {
                  console.log('CAPTCHA expired')
                  setCaptchaToken('')
                  setError('CAPTCHA expired. Please verify again.')
                }}
                options={{
                  theme: 'light',
                  size: 'flexible'
                }}
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-4 rounded-xl text-sm animate-fade-in flex items-start space-x-3">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              </div>
              <div>{error}</div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !password || !confirmPassword || password !== confirmPassword || !captchaToken}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 px-4 rounded-xl font-semibold focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
          >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Updating Password...</span>
                </div>
              ) : (
                'Update Password'
              )}
          </button>
        </form>
      </div>
    </div>
  )
}

export default ResetPassword
